import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';
import { useAuth } from './lib/auth';
import { useCurrentSeason } from './lib/hooks';
import { Loading } from './components/ui';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { MembersPage } from './pages/Members';
import { RegistrationsPage } from './pages/Registrations';
import { MessagesPage } from './pages/Messages';
import { MemberDetailPage } from './pages/MemberDetail';
import { LeaguesPage } from './pages/Leagues';
import { LeagueDetailPage } from './pages/LeagueDetail';
import { SchedulePage } from './pages/Schedule';
import { BonspielsPage } from './pages/Bonspiels';
import { BonspielDetailPage } from './pages/BonspielDetail';
import { WaiversPage } from './pages/Waivers';
import { RentalsPage } from './pages/Rentals';
import { MoneyPage } from './pages/Money';
import { CheckInPage } from './pages/CheckIn';
import { AccessPage } from './pages/Access';
import { PlatformPage } from './pages/Platform';
import { MemberApp } from './member/MemberApp';

export function App() {
  const { user, loading, isOffice } = useAuth();
  const location = useLocation();

  /*
   * The platform console is a different product with a different sign-in,
   * checked before the club gate so that being signed out of Club Office
   * neither blocks it nor is enough to reach it.
   */
  if (location.pathname.startsWith('/platform')) return <PlatformPage />;

  if (loading) {
    return <div className="login"><div className="login__card"><Loading what="Signing you in" /></div></div>;
  }
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location }} />} />
      </Routes>
    );
  }

  /*
   * A plain member gets the member view, not Club Office.
   *
   * They used to get the office shell with every section in the sidebar, of
   * which several answered 403 behind the link — nothing leaked, but it read
   * as a broken admin tool. The member view shows the same five things the
   * companion app shows and nothing more. Anybody with any role beyond
   * 'member' is the office and carries on below.
   */
  if (!isOffice) return <MemberApp />;

  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/registrations" element={<RegistrationsPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/:id" element={<MessagesPage />} />
          <Route path="/members" element={<MembersPage />} />
          <Route path="/members/:id" element={<MemberDetailPage />} />
          <Route path="/leagues" element={<LeaguesPage />} />
          <Route path="/leagues/:id" element={<LeagueDetailPage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/bonspiels" element={<BonspielsPage />} />
          <Route path="/bonspiels/:id" element={<BonspielDetailPage />} />
          <Route path="/waivers" element={<WaiversPage />} />
          <Route path="/check-in" element={<CheckInPage />} />
          <Route path="/access" element={<AccessPage />} />
          <Route path="/rentals" element={<RentalsPage />} />
          <Route path="/money" element={<MoneyPage />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </div>
  );
}

function Sidebar() {
  const { user, signOut, can } = useAuth();
  const season = useCurrentSeason();

  // A badge on the nav, so a waiting registration is never missed.
  const queue = useQuery({
    queryKey: ['registrations', 'pending-count'],
    queryFn: () => api.get<{ total: number }>('/registrations?status=pending&perPage=1'),
    refetchInterval: 120_000,
  });
  const pending = queue.data?.total ?? 0;

  // And one for member messages waiting on a reply.
  const inbox = useQuery({
    queryKey: ['messages', 'waiting-count'],
    queryFn: () => api.get<{ waiting: number }>('/messages/office/threads?state=waiting&perPage=1'),
    refetchInterval: 60_000,
  });
  const waiting = inbox.data?.waiting ?? 0;

  return (
    <nav className="side" aria-label="Club office">
      <a className="side__brand" href="/">
        <img src="/logo.svg" alt="" />
        <span>
          <b>Club Office</b>
          <span>Lindsay Curling</span>
        </span>
      </a>

      <div className="side__group">Curling</div>
      <NavLink to="/" className="nav" end>Dashboard</NavLink>
      <NavLink to="/leagues" className="nav">Leagues &amp; teams</NavLink>
      <NavLink to="/schedule" className="nav">Draws &amp; scores</NavLink>
      <NavLink to="/bonspiels" className="nav">Bonspiels</NavLink>

      <div className="side__group">People</div>
      <NavLink to="/registrations" className="nav">
        Registrations
        {pending > 0 && <span className="badge">{pending}</span>}
      </NavLink>
      <NavLink to="/messages" className="nav">
        Messages
        {waiting > 0 && <span className="badge">{waiting}</span>}
      </NavLink>
      <NavLink to="/members" className="nav">Members</NavLink>
      <NavLink to="/waivers" className="nav">Waivers</NavLink>
      <NavLink to="/check-in" className="nav">Check in</NavLink>

      {can('bar') && (
        <>
          <div className="side__group">The building</div>
          <NavLink to="/rentals" className="nav">Rentals</NavLink>
        </>
      )}

      {can() && (
        <>
          <div className="side__group">Security</div>
          <NavLink to="/access" className="nav">Door access</NavLink>

          <div className="side__group">Money</div>
          <NavLink to="/money" className="nav">Invoices &amp; fees</NavLink>
        </>
      )}

      <div className="side__foot">
        <div className="side__me">
          <b>{user?.fullName}</b>
          <span>{season.data ? `Season ${season.data.name}` : '—'}</span>
        </div>
        <button className="btn btn--sm btn--block" type="button" onClick={signOut}
          style={{ marginTop: '.5rem' }}>
          Sign out
        </button>
      </div>
    </nav>
  );
}

function NotFound() {
  return (
    <>
      <div className="topbar"><h1>Not found</h1></div>
      <div className="content">
        <div className="card"><div className="empty">
          <h3>That page does not exist</h3>
          <p className="small"><a href="/">Back to the dashboard</a></p>
        </div></div>
      </div>
    </>
  );
}

/** Standard page header, used by every screen. */
export function PageHeader(
  { title, sub, actions }:
  { title: string; sub?: React.ReactNode; actions?: React.ReactNode },
) {
  return (
    <div className="topbar">
      <div>
        <h1>{title}</h1>
        {sub && <div className="topbar__sub">{sub}</div>}
      </div>
      {actions && <div className="topbar__actions">{actions}</div>}
    </div>
  );
}
